import { modalActions, placeActions } from '@/actions';
import { Button } from '@/components/elements/forms/buttons';
import HR from '@/components/elements/HR/HR';
import { LoadingIcon } from '@/components/icons';
import BannerSlot from '@/components/modules/banner/BannerSlot';
import Breadcrumbs from '@/components/modules/breadcrumbs/Breadcrumbs';
import { MapTiler } from '@/components/modules/map';
import { DynamicMapProps } from '@/components/modules/map/MapTiler';
import { AndroidTopBanner, IPhoneTopBanner } from '@/components/modules/mobile';
import BookAppointmentModal from '@/components/modules/modals/BookAppointment/BookAppointment';
import { BookAppointmentProvider } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import UserLocationOnboarding from '@/components/modules/modals/UserLocationOnboarding';
import BottomNavBar from '@/components/modules/navigation/BottomNavBar/BottomNavBar';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import NavigationDrawer from '@/components/modules/navigation/NavigationDrawer/NavigationDrawer';
import { SERPTopInfoBanner } from '@/components/modules/pages/serp';
import Pagination from '@/components/modules/pages/serp/Pagination';
import { PlaceCardContainer } from '@/components/modules/place';
import PlaceCardSkeleton from '@/components/skeletons/PlaceCardSkeleton';
import { PLACE_SUBSCRIPTION_TYPE } from '@/constants';
import { BANNER_SLOT_LOCATION } from '@/constants/banner';
import { Footer } from '@/features/footer';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import useKeywordAutoSuggestManager from '@/features/searchV2/components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.hooks';
import { KeywordSelection } from '@/features/searchV2/components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.type';
import useLocationAutoSuggestManager from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.hooks';
import useDateTimePickerManager from '@/features/searchV2/components/DateTimePicker/DateTimePicker.hooks';
import { DateTimePickerState } from '@/features/searchV2/components/DateTimePicker/DateTimePicker.types';
import useFilterManager from '@/features/searchV2/components/Filter/Filter.hooks';
import { MatchingSearchResultsCount } from '@/features/searchV2/components/MatchingSearchResultsCount';
import useSortingManager from '@/features/searchV2/components/Sorting/Sorting.hooks';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '@/features/searchV2/constants';
import { getPageScreenName, trackPlaceClick } from '@/features/searchV2/helpers/tracking';
import NoSearchHits from '@/features/searchV2/modules/NoSearchHits';
import SearchControls, { SearchControlProps } from '@/features/searchV2/modules/SearchControls/SearchControls';
import useSearchControlsManager from '@/features/searchV2/modules/SearchControls/SearchControls.hooks';
import SearchForm from '@/features/searchV2/modules/SearchForm/SearchForm';
import SEO from '@/features/searchV2/modules/SEO';
import { baseTranslationKey } from '@/features/searchV2/pages';
import { getSearchParamsForMatchedServices, hasNextPage } from '@/features/searchV2/pages/index.helpers';
import {
  classnames,
  getSERPBreadcrumbs,
  setBookingStartingPoint,
  showAndroidTopBanner,
  showIphoneTopBanner,
  trackMpMetaEvent,
  trackPage,
} from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { getSearchResultsPageHeadingProps } from '@/helpers/seoTs';
import { locationSlug } from '@/helpers/urlTs';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { DEFAULT_MY_LOCATION_KEYWORD, SEARCH_PAGE_VIEWS } from '@/hooks/search/useSearch.constants';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import { _s, _t } from '@/locale';
import { UNIFIED_SEARCH_ACTION } from '@/reducers/unifiedSearchReducer';
import { placeService, PLACE_IMPRESSION } from '@/services';
import { SearchPlace } from '@/types/api/services/search';
import { Fragment, MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { useCompaniesSearch } from './companies/useCompaniesSearch';
import SerpTabs from './SerpTabs';
import { getSerpTabElementId, getVisibleTabs } from './SerpTabs.helpers';
import { SerpTabId, SerpTabItem, SERP_TAB_IDS } from './SerpTabs.types';
import {
  buildSerpTabHref,
  getOwnTabQuery,
  getSerpSearchIdentity,
  getSerpTabFromPath,
  SERP_TAB_SWITCH_STATE,
} from './unifiedSearch.urls';

const MAP_BASIS = 'basis-[40%]';
const LIST_BASIS = 'basis-[60%]';
const TAB_PANEL_ID = 'serp-tab-panel';
const STICKY_OBSERVER_OPTIONS = { threshold: 1 };

const UnifiedSearchPage = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const navigation = useLocation();
  const activeTab: SerpTabId = getSerpTabFromPath(navigation.pathname) ?? 'places';
  const isCompaniesTab = activeTab === 'places';

  const searchManager = useCompaniesSearch({ isActive: isCompaniesTab });
  const { tabQueries } = useAppSelector((state) => state.unifiedSearch);
  const flags = useAppSelector((state) => state.flags);
  const modal = useAppSelector((state) => state.modal);
  const { show: showNavigationDrawer = false } = useAppSelector((state) => state?.navigationDrawer) as {
    show: boolean;
  };
  const { isMobileView } = useMobileView();
  const megaMenuManager = useMegaMenuContext();

  const controlsAnchorRef = useRef<HTMLDivElement>(null);
  const controlsPosition = useIntersectionObserver(controlsAnchorRef, STICKY_OBSERVER_OPTIONS);
  const isStuck = controlsPosition === 'aboveViewport';
  const tabBarSlotRef = useRef<HTMLDivElement>(null);
  const tabBarHeightRef = useRef(0);

  const {
    q,
    location,
    page,
    prefs,
    startDate,
    endDate,
    timeOfDay,
    paginate,
    viewToggle,
    places,
    profilePages,
    topSearch,
    results,
  } = searchManager;
  const companies = useAppSelector((state) => state.unifiedSearch.companies);
  const { fetchingExtendedPlaces, showInjectionNotice, extendSearchDisabled, bounds } = companies;
  const fetching = isCompaniesTab && Boolean(companies.fetching);

  const search = { q, location, lat: searchManager.lat, lon: searchManager.lon };
  const tabs: SerpTabItem[] = SERP_TAB_IDS.map((id) => ({
    id,
    count: searchManager.counts[id],
    countLoading: searchManager.pendingCounts[id],
    href:
      id === activeTab ? `${navigation.pathname}${navigation.search}` : buildSerpTabHref(id, search, tabQueries[id]),
  }));
  const { tabs: visibleTabs, showTabBar } = getVisibleTabs(tabs, activeTab);

  const premiumProfilePlaces = profilePages.filter(
    (place) => place.settings?.subscriptionType === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_PREMIUM,
  );
  const verifiedProfilePlaces = profilePages.filter(
    (place) => place.settings?.subscriptionType === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_VERIFIED,
  );

  const keywordAutoSuggestManager = useKeywordAutoSuggestManager({
    initialState: {
      keyword: q,
      suggestions: [],
      storedSuggestions: [],
      newServices: [],
      hasHistory: false,
      showSuggestions: true,
    },
    searchType: searchManager.type,
    onSelectKeywordSuggestion: handleOnSelectKeywordSuggestion,
  });

  const locationAutoSuggestManager = useLocationAutoSuggestManager({
    initialState: {
      locationValue: location,
      hasHistory: false,
      suggestions: [],
      storedSuggestions: [],
      showSuggestions: true,
      isFetchingUserPosition: false,
    },
    loadFromHistory: true,
    onSelectLocationSuggestion: handleOnUpdateLocation,
  });

  const searchControlManager = useSearchControlsManager(searchManager);
  const { onChangeAppliedFilters, onChangeSorting, onClearSorting } = searchControlManager;

  const filterManager = useFilterManager({
    initialState: { prefs, showOptions: false },
    urlPrefs: prefs,
    applyOnToggle: !isMobileView,
    onChangeAppliedFilters,
  });

  const sortingManager = useSortingManager({
    initialState: { sort: searchManager.sort, showOptions: false },
    urlSorting: searchManager.sort,
    onChangeSorting,
    onClearSorting,
  });

  const searchControlsProps: SearchControlProps = {
    ...searchControlManager,
    searchManager,
    sortingManager,
    filterManager,
    showSistaminutenToggle: false,
  };

  const dateTimePickerManager = useDateTimePickerManager({
    initialState: {
      startDate: startDate,
      endDate: endDate,
      timeOfDay: timeOfDay,
      showCalendar: false,
    },
    onUpdateDateTime: handleOnUpdateDateTime,
    onClearDateTime: handleOnClearDateTime,
  });

  const showSkeleton = isCompaniesTab && searchManager.status === 'loading';
  const showCompaniesResults = isCompaniesTab && searchManager.status !== 'failed';
  const hasVerifiedProfilePlaces = verifiedProfilePlaces?.length > 0;
  const noMatchingSearchResults = searchManager.status === 'loaded' && !places[page]?.length && !profilePages?.length;
  const showDesktopMap = !isMobileView && isCompaniesTab;
  const topOfSearchPlaces = page < 1 ? topSearch?.places ?? [] : [];
  const topPlacesEntries = !page && topSearch && topSearch.places ? topSearch.places.length : 0;
  const serpPageNo = page ? +page + 1 : 1;
  const source = prefs?.indexOf('5') !== -1 ? 'serp-page-top' : 'serp-page';
  const breadcrumbs = getSERPBreadcrumbs(q, location, 'default');
  const heading = getSearchResultsPageHeadingProps(searchManager);
  const showUserLocationOnboarding = !modal.login?.show && modal.userLocationOnboarding?.show;
  const pageScreenName = getPageScreenName('default', viewToggle.view, isMobileView);
  const hasNextPageResults = hasNextPage(results, page || 0, SEARCH_RESULT_PER_PAGE_LIMIT);
  const canExtendCurrentPage = places[page]?.length <= SEARCH_RESULT_PER_PAGE_LIMIT;
  const shouldRenderExtendedSearch = Boolean(
    !extendSearchDisabled && searchManager.location && !fetching && canExtendCurrentPage && !bounds,
  );
  const isNearMeSearch = locationSlug(searchManager.location || '') === DEFAULT_MY_LOCATION_KEYWORD;

  const mapProps: DynamicMapProps = {
    places: showCompaniesResults
      ? [...(topOfSearchPlaces || []), ...(places[page] || []), ...(hasNextPageResults ? [] : profilePages)]
      : [],
    ...(isNearMeSearch && { fitPlaces: showCompaniesResults ? places[page] || [] : [], fitToPosition: true }),
    fetching: Boolean(fetching || fetchingExtendedPlaces),
    position: (searchManager.lat && searchManager.lon ? `${searchManager.lat}-${searchManager.lon}` : false) as
      | string
      | false,
    onMapBoundsChange: handleOnMapBoundsChange,
    columnClass: MAP_BASIS,
    bounds: companies.bounds,
    boundsFit: companies.boundsFit,
    debugLocation: companies.debugLocation,
    isSistaminutenSearch: false,
    onPlaceClick: (place: SearchPlace, carouselIndex: number) => {
      trackPlaceClick(searchManager, place, carouselIndex, 'search_results_map');
      setBookingStartingPoint('search_results', searchManager.q);
      placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.SERP_TO_PLACE_CLICK);
    },
  };

  const topBannerConfig = flags?.[BANNER_SLOT_LOCATION.TOP_BANNER_SEARCH]?.payload;
  const middleBannerMobileConfig =
    isMobileView &&
    results > 0 &&
    (flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH]?.payload ||
      flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB]?.payload);
  const middleBannerMobileLocationId = flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH]?.payload
    ? BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH
    : BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB;
  const middleBannerDesktopConfig = !isMobileView && flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB]?.payload;

  function handleOnClearDateTime() {
    searchManager.performSearch({ startDate: null, endDate: null, timeOfDay: null });
  }

  function handleOnUpdateDateTime(state: DateTimePickerState) {
    dateTimePickerManager.onCloseCalendar();
    searchManager.performSearch({ startDate: state.startDate, endDate: state.endDate, timeOfDay: state.timeOfDay });
  }

  function handleOnUpdateLocation({ searchContext, ...params }: any) {
    searchManager.performSearch(
      { ...params, q: keywordAutoSuggestManager.keyword },
      {
        bounds: null,
        keywordSubmitted: keywordAutoSuggestManager.keyword !== searchManager.q || undefined,
        searchContext,
      },
    );
  }

  function handleOnSelectKeywordSuggestion(suggestion: KeywordSelection) {
    if (locationAutoSuggestManager.location) {
      keywordAutoSuggestManager.saveKeywordToHistory(suggestion.keyword);
      searchManager.performSearch(
        { location: locationAutoSuggestManager.location, q: suggestion.keyword },
        { keywordSubmitted: true, searchContext: 'search_autocomplete' },
      );
    }
  }

  function handleOnRequestExtendedSearchClick() {
    searchManager.performSearch({}, { extendedSearch: true });
  }

  function handleOnRequestUserLocation() {
    dispatch(modalActions.userLocationOnboarding({ show: false }));
    locationAutoSuggestManager.onFetchAndSetUserLocation(DEFAULT_MY_LOCATION_KEYWORD);
  }

  function handleOnClickPlace(place: SearchPlace, serpIndex: number, isTopOfSearch = false) {
    trackPlaceClick(searchManager, place, serpIndex, pageScreenName, isTopOfSearch);
    setBookingStartingPoint(pageScreenName, searchManager.q);
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.SERP_TO_PLACE_CLICK);
  }

  function handleNextPageClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!hasNextPageResults) return;
    paginate().nextPage();
    window.scrollTo(0, 0);
    e.preventDefault();
  }

  function handlePrevPageClick(e: MouseEvent<HTMLAnchorElement>) {
    if (page <= 0) return;
    paginate().prevPage();
    window.scrollTo(0, 0);
    e.preventDefault();
  }

  function handleOnMapBoundsChange(bounds: string) {
    searchManager.performSearch({ page: 0 }, { bounds });
  }

  function handleTabChange(tab: SerpTabId) {
    if (tab === activeTab) return;
    history.push(tabs.find((item) => item.id === tab).href, SERP_TAB_SWITCH_STATE);
    window.scrollTo(0, 0);
  }

  const prepend = showIphoneTopBanner() ? (
    <IPhoneTopBanner source={pageScreenName} />
  ) : showAndroidTopBanner() ? (
    <AndroidTopBanner source={pageScreenName} />
  ) : null;

  useEffect(() => {
    if (!isMobileView && viewToggle.view === 'map') {
      viewToggle.setView(SEARCH_PAGE_VIEWS[0]);
    }
  }, [isMobileView, viewToggle]);

  useEffect(() => {
    if (!isCompaniesTab && viewToggle.view === 'map') {
      viewToggle.setView(SEARCH_PAGE_VIEWS[0]);
    }
  }, [isCompaniesTab, viewToggle]);

  useEffect(
    () => {
      if (keywordAutoSuggestManager.keyword !== q) {
        keywordAutoSuggestManager.onChange(null, { newValue: q });
      }
      if (locationAutoSuggestManager.location !== location) {
        locationAutoSuggestManager.onChange({ target: { value: location } });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigation],
  );

  useEffect(() => {
    dispatch({
      type: UNIFIED_SEARCH_ACTION.SET_TAB_QUERY,
      payload: {
        tab: activeTab,
        value: { search: getSerpSearchIdentity(search), query: getOwnTabQuery(navigation.search, location) },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  useEffect(() => {
    if (fetching) {
      window.scrollTo(0, 0);
    }
  }, [fetching]);

  useEffect(() => {
    if (!isStuck && tabBarSlotRef.current) {
      tabBarHeightRef.current = tabBarSlotRef.current.offsetHeight;
    }
  });

  useEffect(() => {
    trackMpEvent(
      { name: 'screen_shown', properties: { screen_name: 'search_results', search_variant: activeTab } },
      { sendToCio: false },
    );
  }, [activeTab]);

  useEffect(() => {
    trackPage();
    const search_query = q.trim?.().length > 0 ? q : undefined;
    if (search_query) {
      trackMpMetaEvent('Search', { search_string: search_query });
    }
    return () => {
      dispatch({ type: UNIFIED_SEARCH_ACTION.RESET });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTabBar = (className?: string) => (
    <SerpTabs
      tabs={visibleTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      panelId={TAB_PANEL_ID}
      className={className}
    />
  );

  const searchControlsRow = (
    <>
      <SearchControls {...searchControlsProps} />
      {isMobileView && (
        <>
          {viewToggle.render}
          {/* just a spacer for horizontal scroll */}
          <div className="pl-sm"></div>
        </>
      )}
    </>
  );

  const companiesList = (
    <>
      <div className="space-y-md md:space-y-xl pb-md md:pb-lg">
        <span className="space-x-xs border-border_regular pt-md flex items-center border-t">
          <MatchingSearchResultsCount
            {...searchManager}
            results={results + premiumProfilePlaces.length}
            loading={showSkeleton}
          />
        </span>

        {noMatchingSearchResults && (
          <NoSearchHits
            isSistaminutenSearch={false}
            keyword={q}
            location={location}
            fetchingExtendedPlaces={fetchingExtendedPlaces}
            onExtendSearchClick={handleOnRequestExtendedSearchClick}
          />
        )}
      </div>

      {middleBannerMobileConfig && (
        <div className="py-md">
          <BannerSlot config={middleBannerMobileConfig} locationId={middleBannerMobileLocationId} />
        </div>
      )}
      {middleBannerDesktopConfig && (
        <div className="py-lg">
          <BannerSlot
            config={middleBannerDesktopConfig}
            locationId={BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB}
            inContainer={false}
          />
        </div>
      )}
      <div className="space-y-md">
        {showSkeleton && (
          <div role="status" aria-live="polite" className="space-y-md">
            {Array.from({ length: isMobileView ? 3 : 6 }).map((_, i) => (
              <PlaceCardSkeleton key={`serp-loading-${i}`} />
            ))}
            <span className="sr-only">{_t(`${baseTranslationKey}.loadingSearchResults`)}</span>
          </div>
        )}
        {!showSkeleton &&
          topOfSearchPlaces?.map((place, key) => {
            const serpIndex = key;
            return (
              <PlaceCardContainer
                source="serp-page-top"
                place={place}
                key={serpIndex}
                SerpPageNo={serpPageNo}
                SerpIdx={serpIndex}
                onClickPlace={() => handleOnClickPlace(place, serpIndex, true)}
                fireHover={() => dispatch(placeActions.setHover(place.id))}
                fireHoverOut={() => dispatch(placeActions.removeHover())}
                searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
              />
            );
          })}
        {!showSkeleton &&
          places?.[page]?.map((place, key) => {
            const serpIndex = key + topPlacesEntries;
            return (
              <PlaceCardContainer
                source={source}
                place={place}
                key={serpIndex}
                SerpPageNo={serpPageNo}
                SerpIdx={serpIndex}
                onClickPlace={() => handleOnClickPlace(place, serpIndex, false)}
                fireHover={() => dispatch(placeActions.setHover(place.id))}
                fireHoverOut={() => dispatch(placeActions.removeHover())}
                searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
                isSistaminutenSearch={false}
              />
            );
          })}
        {!showSkeleton &&
          !hasNextPageResults &&
          premiumProfilePlaces.map((place, key) => {
            const serpIndex = key;
            return (
              <PlaceCardContainer
                source={'profilePages'}
                place={place}
                key={serpIndex}
                SerpPageNo={serpPageNo}
                SerpIdx={serpIndex}
                onClickPlace={() => handleOnClickPlace(place, serpIndex, false)}
                fireHover={() => dispatch(placeActions.setHover(place.id))}
                fireHoverOut={() => dispatch(placeActions.removeHover())}
                searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
              />
            );
          })}
      </div>
    </>
  );

  const verifiedProfilesSection = hasVerifiedProfilePlaces && !hasNextPageResults && !showSkeleton && (
    <>
      <div className="py-md px-xl flex">
        <div className="px-lg flex-1">
          <HR className="bg-fg_secondary"></HR>
        </div>
        <div className="text-center">
          <h2 className="text-h-s text-fg_primary">
            {location
              ? _s(`${baseTranslationKey}.relatedCompanies.title`, { location: location })
              : _s(`${baseTranslationKey}.relatedCompanies.titleWithoutLocation`)}
          </h2>
          <span className="text-s text-fg_secondary">{_s(`${baseTranslationKey}.relatedCompanies.description`)}</span>
        </div>
        <div className="px-lg flex-1">
          <HR className="bg-fg_secondary"></HR>
        </div>
      </div>
      <div className="px-md gap-md flex flex-col">
        {verifiedProfilePlaces.map((place, key) => {
          const serpIndex = key;
          return (
            <PlaceCardContainer
              source={'profilePages'}
              place={place}
              key={serpIndex}
              SerpPageNo={serpPageNo}
              SerpIdx={serpIndex}
              onClickPlace={() => handleOnClickPlace(place, serpIndex, false)}
              fireHover={() => dispatch(placeActions.setHover(place.id))}
              fireHoverOut={() => dispatch(placeActions.removeHover())}
              searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
            />
          );
        })}
      </div>
    </>
  );

  const companiesFooterControls = (
    <>
      {shouldRenderExtendedSearch && !noMatchingSearchResults && !hasNextPageResults && !showSkeleton && (
        <div className="p-md w-full text-center">
          <p className="text-xl">{_s('extendSearchTitle')}</p>
          <p className="text-l text-black-600">{_s('extendSearchSubTitle')}</p>
          <Button className="mt-xl" disabled={fetchingExtendedPlaces} onClick={handleOnRequestExtendedSearchClick}>
            <span className="grid items-center" style={{ gridTemplateAreas: 'stack' }}>
              <span className={fetchingExtendedPlaces ? 'invisible' : 'visible'} style={{ gridArea: 'stack' }}>
                {_s('extendButton')}
              </span>
              <span
                className={fetchingExtendedPlaces ? 'visible' : 'invisible'}
                aria-label={_s('extendButtonLoading')}
                style={{ gridArea: 'stack' }}>
                <LoadingIcon style={{ height: 24, width: 24, margin: 'auto' }} />
              </span>
            </span>
          </Button>
        </div>
      )}
      {results > SEARCH_RESULT_PER_PAGE_LIMIT && (
        <Pagination
          currentlyShowing={{
            start: page * SEARCH_RESULT_PER_PAGE_LIMIT + 1,
            end:
              page * SEARCH_RESULT_PER_PAGE_LIMIT + SEARCH_RESULT_PER_PAGE_LIMIT > results
                ? results + (premiumProfilePlaces.length || 0)
                : page * SEARCH_RESULT_PER_PAGE_LIMIT + SEARCH_RESULT_PER_PAGE_LIMIT,
          }}
          totalPages={Math.ceil(results / SEARCH_RESULT_PER_PAGE_LIMIT)}
          nextPage={Boolean(hasNextPageResults) ? handleNextPageClick : undefined}
          prevPage={Boolean(page > 0) ? handlePrevPageClick : undefined}
          totalResults={results + (premiumProfilePlaces.length || 0)}
          page={page}
        />
      )}
    </>
  );

  const View: {
    [key in (typeof SEARCH_PAGE_VIEWS)[number]['view']]: ReactNode;
  } = {
    list: (
      <div className="justify-end md:flex">
        <div
          className={classnames(
            showDesktopMap ? LIST_BASIS : 'w-full',
            !isMobileView && 'pl-[max(calc((100vw-1440px)/2),24px)]',
            !isMobileView && !showDesktopMap && 'pr-[max(calc((100vw-1440px)/2),24px)]',
          )}>
          {isCompaniesTab && showInjectionNotice && q.length > 0 && (
            <SERPTopInfoBanner variant="injectionInfo" important />
          )}
          {topBannerConfig && (
            <BannerSlot config={topBannerConfig} locationId={BANNER_SLOT_LOCATION.TOP_BANNER_SEARCH} />
          )}
          <div className={classnames('container-screen', !isMobileView && 'relative')}>
            <div className="space-y-md md:space-y-xl pb-md md:pb-lg">
              <Breadcrumbs items={breadcrumbs} className="text-sm md:text-base" />
              <h1 className="text-h-m md:text-h-l">
                <span className="text-fg_primary font-semibold">{heading.keyword},</span>
                <span className="text-fg_secondary"> {heading.location.replace(' ', ' ')}</span>
              </h1>
              {heading.description && <p className="text-fg_secondary text-l">{heading.description}</p>}
              <div ref={controlsAnchorRef} className="space-y-md">
                {showTabBar && (
                  <div
                    ref={tabBarSlotRef}
                    style={isStuck ? { minHeight: tabBarHeightRef.current } : undefined}
                    aria-hidden={isStuck || undefined}>
                    {!isStuck && renderTabBar()}
                  </div>
                )}
                {isCompaniesTab && <div className="gap-x-sm flex items-start">{searchControlsRow}</div>}
              </div>
            </div>

            <div
              id={TAB_PANEL_ID}
              role={showTabBar ? 'tabpanel' : undefined}
              aria-labelledby={showTabBar ? getSerpTabElementId(TAB_PANEL_ID, activeTab) : undefined}>
              {showCompaniesResults && companiesList}
            </div>
          </div>

          {showCompaniesResults && verifiedProfilesSection}

          <div className="container-screen">
            <div className="pb-lg">
              {showCompaniesResults && companiesFooterControls}
              <Footer />
            </div>
          </div>
        </div>
        {showDesktopMap && (
          <div className={classnames('flex-1', MAP_BASIS)}>
            <MapTiler {...mapProps} />
          </div>
        )}
      </div>
    ),
    map: <MapTiler {...mapProps} columnClass="full" />,
  };

  const megaMenu = (
    <MegaMenu
      {...megaMenuManager}
      renderNavigationContainer={(props) => {
        const ref = props.navigationContainerRef.current;
        return ref ? (
          createPortal(<MegaMenu.Categories {...props} />, ref)
        ) : (
          <div id={props.navigationContainerId} className="mega-menu-navigation-container" role="navigation">
            <MegaMenu.Categories {...props} />
          </div>
        );
      }}
    />
  );
  const megaMenuHeight = megaMenuManager.navRef.current?.getBoundingClientRect()?.height ?? 56;

  return (
    <div className="bg-surface_l4">
      <NavBar
        source="list"
        navClass="list-container navigation"
        defaultNavBarHeight={isMobileView ? 120 : 128}
        prepend={prepend ? { element: prepend } : undefined}
        view={viewToggle.view}
        headerSlot={
          <div className="pb-sm">
            <div className="px-lg w-full xl:w-8/12">
              <SearchForm
                searchManager={searchManager}
                keywordAutoSuggestManager={keywordAutoSuggestManager}
                locationAutoSuggestManager={locationAutoSuggestManager}
                dateTimePickerManager={dateTimePickerManager}
              />
            </div>
            {isCompaniesTab && (
              <div
                className={classnames(
                  'pl-lg mt-sm space-x-sm flex',
                  isMobileView && 'no-scrollbar flex overflow-x-scroll',
                )}>
                {searchControlsRow}
              </div>
            )}
          </div>
        }
        middleSlot={
          <SearchForm
            searchManager={searchManager}
            keywordAutoSuggestManager={keywordAutoSuggestManager}
            locationAutoSuggestManager={locationAutoSuggestManager}
            dateTimePickerManager={dateTimePickerManager}
          />
        }
        bottomSlot={
          <Fragment>
            {viewToggle.view === 'list' && (
              <>
                {!(isStuck && isMobileView) &&
                  (megaMenuManager.activeTab ? (
                    megaMenu
                  ) : (
                    <HideOnScrollWrapper height={megaMenuHeight}>{megaMenu}</HideOnScrollWrapper>
                  ))}
                {isStuck &&
                  showTabBar &&
                  (isMobileView ? renderTabBar('-mx-md px-md') : <div className="px-lg">{renderTabBar()}</div>)}
                {isStuck && isMobileView && isCompaniesTab && (
                  <div className="space-x-sm no-scrollbar pb-sm pt-xxs flex overflow-x-scroll">{searchControlsRow}</div>
                )}
              </>
            )}
            {viewToggle.view === 'map' && (
              <div className="space-x-sm no-scrollbar pb-sm pt-xxs flex overflow-x-scroll">{searchControlsRow}</div>
            )}
          </Fragment>
        }
        floatingSlot={<Fragment>{megaMenuManager.renderNavigationContainer(megaMenuManager)}</Fragment>}
      />

      {View[viewToggle.view]}

      <BookAppointmentModal />

      {showUserLocationOnboarding && (
        <UserLocationOnboarding
          isOpen={showUserLocationOnboarding}
          onClose={() => dispatch(modalActions.userLocationOnboarding({ show: false }))}
          onRequestUserLocation={handleOnRequestUserLocation}
        />
      )}
      {isCompaniesTab && <SEO {...searchManager} />}
      {isMobileView && !showNavigationDrawer && <NavigationDrawer />}
      {isMobileView && showNavigationDrawer && <BottomNavBar source="list" />}
    </div>
  );
};

const UnifiedSearchPageWithProviders = () => (
  <BookAppointmentProvider>
    <UnifiedSearchPage />
  </BookAppointmentProvider>
);

export default UnifiedSearchPageWithProviders;
